import React, { useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@material-ui/core/Typography';
import {
  ActionButtonHandle,
  FormPanel,
  RemoteTableField,
  ScrollableSource,
  TableFieldHandle,
  TableFieldToolbarProps,
} from '@eas/common-web';
import { useAutoCollapse } from '@components/form/misc/auto-collapse-hook';
import { FormTableCount } from '@components/form/table-field/form-table-count';
import { AccessoryParticipant, EsFile } from '@models';
import { useAccessoryParticipantColumns } from './accessory-participant-columns';
import { FormFields } from './accessory-participant-dialog';
import { AccessoryParticipantToolbar } from './accessory-participant-toolbar';

export interface AccessoryParticipantFieldProps {
  disabled: boolean;
  esFile: EsFile | undefined;
  source?: ScrollableSource<AccessoryParticipant>;
}

export function AccessoryParticipantField({
  disabled,
  esFile,
  source,
}: AccessoryParticipantFieldProps) {
  const { panelRef } = useAutoCollapse(source?.items ?? []);

  const columns = useAccessoryParticipantColumns();

  const tableRef = useRef<TableFieldHandle>(null);
  const editRef = useRef<ActionButtonHandle>(null);

  const ToolbarComponent = useMemo(
    () =>
      function Toolbar(props: TableFieldToolbarProps) {
        return (
          <AccessoryParticipantToolbar
            {...props}
            ref={editRef}
            disabled={disabled}
            esFile={esFile}
          />
        );
      },
    [disabled, esFile]
  );

  if (!source) {
    return <> Missing ACCESSORY_PARTICIPANT_SOURCE </>;
  }

  return (
    <FormPanel
      label={
        <FormattedMessage
          id="ES__ACCESSORY_PARTICIPANTS__PANEL_TITLE"
          defaultMessage="Vedlejší účastníci"
        />
      }
      expandable={true}
      ref={panelRef}
      summary={<FormTableCount source={source} />}
    >
      <RemoteTableField
        ref={tableRef}
        source={source}
        // disable sorting localy - #USCR-2415, needed for e.g. address field
        columns={columns.map((c) => ({ ...c, sortkey: undefined }))}
        onRowClick={(_, index) => {
          if (!disabled) {
            tableRef.current?.setSelectedIndex(index);
            editRef.current?.executeAction();
          }
        }}
        maxRows={10}
        ToolbarComponent={ToolbarComponent}
        disabled={disabled}
        showDetailBtnCond={() => true}
        FormFieldsComponent={() => (
          <div style={{ width: 700 }}>
            <Typography variant="body1" style={{ marginBottom: 8 }}>
              <FormattedMessage
                id="ES__ACCESSORY_PARTICIPANTS__DESCRIPTION"
                defaultMessage="Vedlejší účastník:"
              />
            </Typography>
            <FormFields />
          </div>
        )}
      />
    </FormPanel>
  );
}
